import { motion } from 'framer-motion';
import { FC, JSX, useCallback, useMemo, useState } from 'react';

import TabLine from '@semcore/ui/tab-line';
import { Hint } from '@semcore/ui/tooltip';

import ThemeSwitch from '@/features/theme-switch';
import { IconButton, Tooltip } from '@/shared';
import Arrow from '@/shared/ui/icons/Arrow';
import Edit2Icon from '@/shared/ui/icons/Edit2';
import LogoIcon from '@/shared/ui/icons/LogoIcon';
import LogoText from '@/shared/ui/icons/LogoText';
import OverlayIcon from '@/shared/ui/icons/Overlay';
import PlayerIcon from '@/shared/ui/icons/Player';

import { isStudioInDevMode } from '../lib/devMode';

const tabItemTooltipText =
  'Render the video before configuring the player or overlay.';

interface ISidebarProps {
  selectedTab: number;
  setSelectedTab: (index: number) => void;
  open?: boolean;
  onToggleChange: (open: boolean) => void;
  disabled?: boolean;
  videoConfigurationDisabled?: boolean;
  mode?: 'create' | 'edit';
  type: 'video' | 'slider';
  renderButtonDisabled?: boolean;
}

const Sidebar: FC<ISidebarProps> = ({
  selectedTab,
  setSelectedTab,
  open = false,
  onToggleChange,
  disabled = false,
  videoConfigurationDisabled = false,
  mode = 'create',
  type,
  renderButtonDisabled = false,
}): JSX.Element => {
  const [openSidebar, setOpenSidebar] = useState(true);
  const sidebarWidth = useMemo(
    () => (openSidebar ? 'w-32' : 'w-[72px]'),
    [openSidebar]
  );
  const pl = useMemo(() => (openSidebar ? 0 : 8), [openSidebar]);
  const decreaseWidth = useMemo(
    () => ({
      transform: openSidebar ? 'scaleX(1)' : 'scaleX(0)',
      width: openSidebar ? '100%' : 0,
    }),
    [openSidebar]
  );
  const growWidth = useMemo(
    () => ({
      transform: openSidebar ? 'scaleX(0)' : 'scaleX(1)',
      width: openSidebar ? 0 : '100%',
    }),
    [openSidebar]
  );
  const pb = useMemo(() => (openSidebar ? 12 : 40), [openSidebar]);
  const rotateY = useMemo(() => (openSidebar ? 0 : 180), [openSidebar]);
  const top = useMemo(() => (openSidebar ? 8 : 44), [openSidebar]);
  const toggle = useCallback(() => {
    setOpenSidebar(!openSidebar);
    onToggleChange(!openSidebar);
  }, [onToggleChange, openSidebar]);

  return (
    <div className={`flex flex-col p-4 ${sidebarWidth}`}>
      <motion.div
        initial={{ paddingBottom: 20 }}
        animate={{ paddingBottom: 20 }}
        className="relative flex items-center justify-between pb-4"
      >
        <motion.div
          initial={{ paddingLeft: 0 }}
          animate={{ paddingLeft: pl }}
          className="flex items-center"
        >
          <motion.div
            initial={{ transform: 'scaleX(0)' }}
            animate={decreaseWidth}
            className="overflow-hidden"
          >
            <LogoText />
          </motion.div>
          <motion.div
            initial={{ transform: 'scaleX(0)' }}
            animate={growWidth}
            className="overflow-hidden"
          >
            <LogoIcon />
          </motion.div>
        </motion.div>
      </motion.div>

      <div className="flex grow pt-2">
        <TabLine
          size="m"
          underlined={false}
          value={selectedTab}
          onChange={setSelectedTab}
          className="flex flex-col gap-2"
        >
          <Hint placement="right" title="Edit" disabled={openSidebar}>
            <TabLine.Item
              value={0}
              className="m-0 !flex !h-9 w-full !justify-start gap-2 rounded !p-2 before:hidden after:hidden hover:bg-blue/5 hover:!text-blue disabled:cursor-not-allowed aria-selected:bg-blue/5 aria-selected:text-blue dark:text-gray"
              disabled={disabled}
            >
              <TabLine.Item.Addon className="!m-0">
                <Edit2Icon active={selectedTab === 0} />
              </TabLine.Item.Addon>
              <TabLine.Item.Text
                className={`text-sm font-light leading-6 ${openSidebar ? '' : '!hidden'}`}
              >
                {mode === 'create' ? 'Create' : 'Edit'}
              </TabLine.Item.Text>
            </TabLine.Item>
          </Hint>

          {type === 'video' && openSidebar && !isStudioInDevMode && (
            <Tooltip
              disabled={!renderButtonDisabled}
              title="Add clips to the timeline before rendering."
            >
              <TabLine.Item
                value={3}
                className="m-0 !flex !h-7 w-full gap-2 rounded !bg-blue !p-2 text-center !text-white before:hidden after:hidden hover:!bg-blue/[.8] disabled:cursor-not-allowed"
                disabled={renderButtonDisabled}
              >
                <TabLine.Item.Text className="text-sm font-medium leading-6">
                  Render
                </TabLine.Item.Text>
              </TabLine.Item>
            </Tooltip>
          )}

          <Hint
            placement="right"
            title={videoConfigurationDisabled ? tabItemTooltipText : 'Player'}
            disabled={
              type === 'video'
                ? !videoConfigurationDisabled && openSidebar
                : true
            }
          >
            <TabLine.Item
              value={1}
              className="m-0 !flex !h-9 w-full !justify-start gap-2 rounded !p-2 before:hidden after:hidden hover:bg-blue/5 hover:!text-blue disabled:!cursor-not-allowed aria-selected:bg-blue/5 aria-selected:text-blue dark:text-gray"
              disabled={type === 'video' ? videoConfigurationDisabled : false}
            >
              <TabLine.Item.Addon className="!m-0">
                <PlayerIcon active={selectedTab === 1} />
              </TabLine.Item.Addon>
              <TabLine.Item.Text
                className={`text-sm font-light leading-6 ${openSidebar ? '' : '!hidden'}`}
              >
                Player
              </TabLine.Item.Text>
            </TabLine.Item>
          </Hint>

          {type === 'video' && (
            <Hint
              placement="right"
              title={
                videoConfigurationDisabled ? tabItemTooltipText : 'Overlay'
              }
              disabled={!videoConfigurationDisabled && openSidebar}
            >
              <TabLine.Item
                value={2}
                className="m-0 !flex !h-9 w-full !justify-start gap-2 rounded !p-2 before:hidden after:hidden hover:bg-blue/5 hover:!text-blue disabled:cursor-not-allowed aria-selected:bg-blue/5 aria-selected:text-blue dark:text-gray"
                disabled={videoConfigurationDisabled}
              >
                <TabLine.Item.Addon className="!m-0">
                  <OverlayIcon active={selectedTab === 2} />
                </TabLine.Item.Addon>
                <TabLine.Item.Text
                  className={`text-sm font-light leading-6 ${openSidebar ? '' : '!hidden'}`}
                >
                  Overlay
                </TabLine.Item.Text>
              </TabLine.Item>
            </Hint>
          )}
        </TabLine>
      </div>

      <motion.div
        initial={{ paddingBottom: 12 }}
        animate={{ paddingBottom: pb }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`flex items-center ${open ? '' : 'justify-center '}relative`}
      >
        <div>
          <IconButton
            className="opacity-0 min-[962px]:opacity-100"
            title={open ? <span className="hidden pr-2">Collapse</span> : ''}
            icon={
              <motion.div
                initial={{ rotateY: 0 }}
                animate={{ rotateY }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <Arrow />
              </motion.div>
            }
            onClick={toggle}
            open={open}
          />
        </div>
        <motion.div
          initial={{ top: 0 }}
          animate={{ top }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="absolute right-0"
        >
          <ThemeSwitch />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Sidebar;
